import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * OnboardingStepSheet (iter164)
 *
 * Bottom-rising popup used by every onboarding step (Step 1, all
 * Step 2 sub-steps, every Step 3 agreement variant). Standardised so
 * the user sees the SAME visual format across every step.
 *
 * Behaviour:
 *   - Rises from the bottom to ~80 vh (4/5 of viewport)
 *   - Rounded top corners
 *   - Solid background color BEHIND the sheet (admin-customisable
 *     hex via the new step-meta endpoint). Falls back to a heavy
 *     translucent dark backdrop when no color set.
 *   - Drag-handle bar at the top: tap it OR drag down to close.
 *   - Mounted in a portal-style fixed wrapper at z-index 9000 so it
 *     sits above the layout but below celebration overlays (which
 *     run at 99999).
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   title: string
 *   bgColor: hex string (e.g. "#0F2A1A") OR falsy → translucent
 *   contentClassName: optional extra classes for inner scroll area
 *   children: the step's body content (the form, the embedded PDF
 *             iframe, the video player, etc.)
 */
const OnboardingStepSheet = ({
  open,
  onClose,
  title,
  bgColor,
  contentClassName = '',
  children,
  fullBleed = false,
  darkBody = false,
  hideCloseButton = false,
  testId = 'onboarding-step-sheet',
}) => {
  const sheetRef = useRef(null);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startYRef = useRef(0);

  // Reset drag on (re)open so a previously-closed-by-drag sheet
  // doesn't open already half-off-screen.
  useEffect(() => {
    if (open) {
      setDragY(0);
      setDragging(false);
    }
  }, [open]);

  // Esc to close.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const onPointerDown = useCallback((e) => {
    startYRef.current = (e.touches ? e.touches[0].clientY : e.clientY) || 0;
    setDragging(true);
  }, []);

  const onPointerMove = useCallback((e) => {
    if (!dragging) return;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) || 0;
    const delta = y - startYRef.current;
    if (delta > 0) setDragY(delta);
  }, [dragging]);

  const onPointerUp = useCallback(() => {
    if (!dragging) return;
    setDragging(false);
    if (dragY > 120) {
      onClose?.();
    } else {
      setDragY(0);
    }
  }, [dragging, dragY, onClose]);

  useEffect(() => {
    if (!dragging) return undefined;
    const move = (e) => onPointerMove(e);
    const up = () => onPointerUp();
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move, { passive: true });
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
    };
  }, [dragging, onPointerMove, onPointerUp]);

  if (!open) return null;

  const backdropStyle = bgColor
    ? { backgroundColor: bgColor, opacity: 1 }
    : { backgroundColor: 'rgba(8, 14, 10, 0.6)', backdropFilter: 'blur(10px)' };

  // iter175by — Portal the sheet straight into <body>. Mounting it
  // inline keeps it INSIDE the PullToRefresh wrapper around HomePage,
  // which applies `transform: translateY(...)` to its inner <div>.
  // A `transform` ancestor:
  //   (a) creates a new stacking context — so `z-index: 9000` only
  //       beats siblings inside that context, NOT the bottom-nav
  //       (`z-50`) sitting at the document root. Result: nav covered
  //       the sheet.
  //   (b) becomes the containing block for any `position: fixed`
  //       descendant. `inset: 0` then anchors to the wrapper's box
  //       (not the viewport), which is what produced the user's
  //       reported "distorted" sheet position/dimensions.
  // Portaling to <body> sidesteps both problems — the sheet now
  // lives at the root of the DOM with no transform ancestor.
  const sheet = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9000,
        ...backdropStyle,
      }}
      data-testid={`${testId}-backdrop`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={sheetRef}
        className={`absolute left-0 right-0 mx-auto shadow-2xl flex flex-col ${darkBody ? 'bg-black' : 'bg-white'}`}
        style={{
          // Popup rises high up the viewport — the parent backdrop
          // is `position: fixed; inset: 0` (= visible viewport on
          // iOS too), so anchoring `top: 8%; bottom: 0` makes the
          // sheet ~92% of the actually-visible area. Using % of the
          // backdrop (not vh) sidesteps iOS Safari's dynamic
          // browser-bar quirk.
          top: '8%',
          bottom: 0,
          maxWidth: 720,
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          transform: `translateY(${dragY}px)`,
          transition: dragging ? 'none' : 'transform 280ms cubic-bezier(0.32, 0.72, 0, 1)',
          willChange: 'transform',
        }}
        data-testid={testId}
      >
        {/* Drag handle bar — tap OR drag to close */}
        <div
          className="pt-2 pb-1 flex flex-col items-center cursor-grab active:cursor-grabbing select-none flex-shrink-0"
          onMouseDown={onPointerDown}
          onTouchStart={onPointerDown}
          data-testid={`${testId}-handle`}
        >
          <div className={`w-12 h-1.5 rounded-full ${darkBody ? 'bg-white/30' : 'bg-gray-300'}`} />
        </div>
        {fullBleed ? (
          // Full-bleed mode: the embedded page IS the popup. No title
          // bar, no body padding — iframe fills the popup. A small
          // floating close X sits in the top-right corner above the
          // iframe so the user can still dismiss without dragging.
          <>
            {!hideCloseButton && (
              <button
                type="button"
                className={`absolute top-2 right-2 z-10 p-1.5 rounded-full backdrop-blur shadow-sm ${darkBody ? 'bg-black/55 hover:bg-black/70 text-white' : 'bg-white/80 hover:bg-white'}`}
                onClick={onClose}
                aria-label="Close"
                data-testid={`${testId}-close-btn`}
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <div className={`flex-1 overflow-hidden ${contentClassName}`} data-testid={`${testId}-body`}>
              {children}
            </div>
          </>
        ) : (
          <>
            <div className="px-5 pt-1 pb-3 border-b flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-semibold truncate">{title}</h2>
              <button
                type="button"
                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={onClose}
                aria-label="Close"
                data-testid={`${testId}-close-btn`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className={`flex-1 overflow-y-auto px-5 py-4 ${contentClassName}`} data-testid={`${testId}-body`}>
              {children}
            </div>
          </>
        )}
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(sheet, document.body)
    : sheet;
};

export default OnboardingStepSheet;
